import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import WithRoutify from "../../../hoc/WithRoutify";
import { isSet } from "../../../utils/commonUtils";
import Icons from "../../../components/UI/Icons";
import Buttons from "../../../components/UI/Buttons";
import ProviderService from "../../../services/ProviderService";
import VisitsService from "../../../services/VisitsService";
import OrganizationService from "../../../services/OrganizationService";
import SelectList from "../../../components/UI/FormInputs/SelectList";
import { OK } from "../../../utils/constant";

const NewProviderDetails = (props) => {
  const { visitDetails, visit_id, fetchVisitDetails } = props;
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [loading, setLoading] = useState(false);

  const providerDetails = visitDetails?.primaryProvider || visitDetails?.primary_provider;
  const clinicId = visitDetails?.clinic_id || visitDetails?.employee?.clinic?.clinic_id || visitDetails?.clinic?.clinic_id;

  const inputFields = providerDetails ? [
    {
      label: "Name",
      name: "name",
      value: `${providerDetails.first_name || ''} ${providerDetails.last_name || ''}`.trim(),
    },
    {
      label: "Type",
      name: "type",
      value: providerDetails.type || providerDetails.provider_type,
    },
    {
      label: "License",
      name: "license",
      value: providerDetails.license,
    },
    {
      label: "NPI",
      name: "npi",
      value: providerDetails.npi,
    },
  ] : [];

  useEffect(() => {
    if (showAssignModal && clinicId) {
      fetchProviders();
    }
  }, [showAssignModal, clinicId]);

  const fetchProviders = async () => {
    if (!clinicId) {
      if (props.Toast) {
        props.Toast.error("Clinic information not available");
      }
      return;
    }

    setLoading(true);
    try {
      const data = await ProviderService.getProviderByClinicId(OrganizationService.getOrgId(), clinicId);
      if (data && Array.isArray(data)) {
        const options = [{ label: '-Select Provider-', value: '' }];
        data.forEach((provider) => {
          options.push({
            label: `${provider.first_name || ''} ${provider.last_name || ''}`.trim(),
            value: provider.provider_id,
          });
        });
        setProviders(options);
      }
    } catch (error) {
      console.error("Error fetching providers:", error);
      if (props.Toast) {
        props.Toast.error("Failed to fetch providers");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAssignProvider = () => {
    setShowAssignModal(true);
    setSelectedProvider('');
  };

  const handleSaveProvider = async () => {
    if (!selectedProvider) {
      if (props.Toast) {
        props.Toast.error("Please select a provider");
      }
      return;
    }

    setLoading(true);
    try {
      const response = await VisitsService.updateProvider(visit_id, {
        primary_provider_id: selectedProvider,
      });

      if (response && (response.code === OK || response.code === 200 || response.code === 'OK')) {
        if (props.Toast) {
          props.Toast.success("Provider assigned successfully");
        }
        setShowAssignModal(false);
        if (fetchVisitDetails) {
          fetchVisitDetails(visit_id);
        }
      } else {
        if (props.Toast) {
          props.Toast.error(response?.data?.error || response?.data || "Failed to assign provider");
        }
      }
    } catch (error) {
      console.error("Error assigning provider:", error);
      if (props.Toast) {
        props.Toast.error(error.message || "Failed to assign provider");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowAssignModal(false);
    setSelectedProvider('');
  };

  return (
    <>
      <div className="sh_cardBox mb-3 h-auto sh_cardbglight">
        <div className="sh_cardHeader p-2">
          <div className="d-flex align-items-center sh_emrproviderdetailsheader justify-content-between">
            <h6 className="text-md font-weight-bold mb-0">
              Provider Details
            </h6>
            {!providerDetails && (
              <Buttons
                className="btn btn-sm btn-primary"
                label="Assign Provider"
                acl="any"
                clickHandler={handleAssignProvider}
              />
            )}
          </div>
        </div>
        <div className="sh_cardBody py-0">
          {providerDetails ? (
            <div className="row">
              {inputFields.map((field, fieldInd) => (
                <React.Fragment key={fieldInd}>
                  <div className="col-lg-6 col-md-12 col-12 pt-2">
                    <label className="f-400 text-sm text-truncate mb-0 opacity-75">{field.label}</label>
                  </div>
                  <div className="col-lg-6 col-md-12 col-12 pt-2">
                    <h6 className="font-weight-bold text-sm text-lg-right text-md-left text-truncate mb-0" title={field.value}>
                      {isSet(field.value, '--')}
                    </h6>
                  </div>
                </React.Fragment>
              ))}
            </div>
          ) : (
            <div className="text-center py-3">
              <p className="text-sm text-muted mb-0">No provider assigned</p>
            </div>
          )}
        </div>
      </div>

      {showAssignModal && (
        createPortal(
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Assign Provider</h5>
                  <button
                    type="button"
                    className="close"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Select Provider</label>
                    <SelectList
                      name="provider"
                      options={providers}
                      value={selectedProvider}
                      changeHandler={(value) => setSelectedProvider(value)}
                      disabled={loading}
                      className="form-control"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveProvider}
                    disabled={loading || !selectedProvider}
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )
      )}
    </>
  );
};

export default WithRoutify(NewProviderDetails);

